import { useEffect, useRef } from 'react';

// Head pose from BlazeFace (@tensorflow-models/blazeface) — a small, stable face
// detector that returns real PIXEL coordinates on tfjs 4.x. We previously tried
// @tensorflow-models/face-detection's tfjs runtime, but it returned detections
// with all-zero geometry (a known incompatibility with tfjs 4.x).
//
// BlazeFace landmark order: [rightEye, leftEye, nose, mouth, rightEar, leftEar].
const KP_NAMES = ['rightEye', 'leftEye', 'noseTip', 'mouthCenter', 'rightEarTragion', 'leftEarTragion'];

function pt(v) {
  // topLeft/bottomRight/landmark entries are [x, y] arrays when returnTensors=false.
  return Array.isArray(v) ? v : [v[0], v[1]];
}

function computePose(pred, w, h) {
  const [x1, y1] = pt(pred.topLeft);
  const [x2, y2] = pt(pred.bottomRight);
  const lm = (pred.landmarks || []).map(pt);
  const keypoints = lm.map((p, i) => ({ x: p[0], y: p[1], name: KP_NAMES[i] }));
  const box = { xMin: x1, yMin: y1, xMax: x2, yMax: y2, width: x2 - x1, height: y2 - y1 };

  let yaw = 0;
  let pitch = 0;
  const re = lm[0];
  const le = lm[1];
  const nose = lm[2];
  if (re && le && nose) {
    const eyeMidX = (le[0] + re[0]) / 2;
    const eyeMidY = (le[1] + re[1]) / 2;
    const eyeDist = Math.abs(le[0] - re[0]) || 1;
    yaw = (nose[0] - eyeMidX) / eyeDist; // + = nose toward image-right
    pitch = (nose[1] - eyeMidY) / eyeDist; // + = nose lower (head down)
  }
  return { faceFound: true, yaw, pitch, box, keypoints, width: w, height: h };
}

export default function useHeadPose({ ready } = {}) {
  const modelRef = useRef(null);
  const enabledRef = useRef(false);
  const statusRef = useRef('loading'); // 'loading' | 'ready' | 'unavailable'
  const busyRef = useRef(false);
  const lastRef = useRef(null);
  const loggedRef = useRef(false);

  useEffect(() => {
    if (!ready) return undefined;
    let cancelled = false;

    (async () => {
      try {
        const tf = await import('@tensorflow/tfjs');
        try {
          await tf.setBackend('webgl');
        } catch (e) {
          /* keep default backend */
        }
        await tf.ready();
        const blazeface = await import('@tensorflow-models/blazeface');
        const model = await blazeface.load();
        if (cancelled) return;
        modelRef.current = model;
        enabledRef.current = true;
        statusRef.current = 'ready';
        console.log('[useHeadPose] BlazeFace ready, backend:', tf.getBackend());
      } catch (err) {
        console.warn('useHeadPose: face detection unavailable; wizard uses manual capture', err);
        enabledRef.current = false;
        statusRef.current = 'unavailable';
      }
    })();

    return () => {
      cancelled = true;
      modelRef.current = null;
      enabledRef.current = false;
      lastRef.current = null;
    };
  }, [ready]);

  const detect = (video) => {
    if (!enabledRef.current || !modelRef.current || !video || !video.videoWidth || busyRef.current) return;
    busyRef.current = true;
    let promise;
    try {
      // estimateFaces(input, returnTensors=false, flipHorizontal=false, annotateBoxes=true)
      promise = modelRef.current.estimateFaces(video, false, false, true);
    } catch (e) {
      busyRef.current = false;
      if (!loggedRef.current) {
        loggedRef.current = true;
        console.warn('[useHeadPose] estimateFaces threw synchronously', e);
      }
      return;
    }
    promise
      .then((preds) => {
        if (!loggedRef.current) {
          loggedRef.current = true;
          console.log('[useHeadPose] first estimate — faces found:', preds ? preds.length : 0);
        }
        lastRef.current = preds && preds.length
          ? computePose(preds[0], video.videoWidth, video.videoHeight)
          : { faceFound: false };
      })
      .catch((e) => {
        if (!loggedRef.current) {
          loggedRef.current = true;
          console.warn('[useHeadPose] estimateFaces error', e);
        }
      })
      .finally(() => {
        busyRef.current = false;
      });
  };

  return {
    detect,
    getPose: () => lastRef.current,
    isEnabled: () => enabledRef.current,
    getStatus: () => statusRef.current,
  };
}
